import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { createLogger } from '~/utils/logging';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { PERIOD_LIST_ALL_QUERY_KEY } from '~/services/period';
import { useHttpClient } from '~/hooks/useHttpClient';

const log = createLogger('usePeriodDeleteMutation');

export type DeletePeriodRequest = {
  periodId: string;
};

export type DeletePeriodResponse = {
  periodId: string;
};

export function usePeriodDeleteMutation() {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: DeletePeriodRequest) => {
      log.debug('Deleting period', { request });
      const { periodId } = await httpClient.post<DeletePeriodResponse>('/period/delete', request);
      if (periodId) {
        log.info('Deleted period successfully', {
          periodId,
        });
      }
      return { periodId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERIOD_LIST_ALL_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to delete period', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return {
    data,
    error,
    mutate,
    reset,
    status,
  };
}
